/**
 * 中奖、红包、广告弹窗
 * 弹出优先级：真实体育中奖>彩蛋>红包生效提示>广告弹窗
 */

import Vue from 'vue'
import { useEventBus } from '@vueuse/core'
import customPush from 'custom-push'
import cookie from '@ms/utils/cookie.js'
import VDialog from 'fe-vui/dialog'
import { sleep } from 'wap/utils/utils.js'
import Winning from './pagelet/Winning'
import PopupGiveaway from './pagelet/PopupGiveaway'
import ConditionalVoucher from './pagelet/ConditionalVoucher.vue'
import PopupVoucher from 'wap/components/PopupVoucher'
import eventBus from '@ms/utils/event-bus.js'
import { BETSLIP_VISIBLE } from 'wap/components/Betslip/event-bus-type.js'
import PushWinning from 'wap/views/virtual/pagelet/PushWinning.vue'

import './style/dialog.less'
import modalHelper from '@ms/utils/scroll-fixed'
import { checkLoginStatus, setSessionLoginStatus } from '@ms/utils/auth.js'
import { isApp } from 'wap/hybrid/device.js'

let vm = new Vue()

/**
 * 用户推送弹窗
 * 规则：
  1. 优先级：Live and Prematch product down 弹窗 > realsport 中奖  = virtual 中奖 > 彩蛋 > voucher > 首页弹窗广告。真实体育和virtual 的优先级相同，但是真实体育中奖弹窗不在虚拟体育页面弹出，虚拟体育中奖弹窗只在虚拟体育页面弹出。彩蛋和优惠券的弹窗不受上述条件限制，不区分虚拟体育真实体育。广告弹窗只在首页弹出。

  2. 用户登入活跃状态：同时存在多个弹窗，按照优先级弹出，低优先级不弹出。若用户停留在某个弹窗未关闭：同级别或更高级别弹窗推送时，覆盖当前弹窗，当前弹窗关闭；低级别弹窗推送时，不弹出，维持当前弹窗状态。（最终的结果是，用户始终只用手动关闭一个弹窗）（wap 已实现，app暂时未实现在调整）

  3. 用户处于初登录状态：同时存在多种弹窗等待弹出，优先级排序，3秒内，优先弹出高优先级弹窗，其他弹窗将不再弹出；3秒后新入的其他弹窗正常弹出 。其中：中奖弹窗弹出金额最大的一个；彩蛋和voucher弹窗弹出推送时间最近的一个；广告弹窗弹出推送时间最近的一个；（wap 暂时未实现，app已实现）

  4. 用户处在realsport 的betslip 页面&cashout页面&充值提现页面，所有弹窗不弹出。等回到首页后，再将推送弹出，推送弹出机制从前面几条。virtual 页面betslip展开，弹窗不弹出
 */
const amazingPush = {
  _topicMap: {
    realsport: 'personal_winning_topic',
    virtual: 'personal_virtual_winning_topic',
    voucher: 'user_voucher_receive_topic',
    quest: 'user_quest_receive_topic'
  },
  // 推送回调函数集
  _ref: {},
  // 是否处于暂停状态
  paused: false,
  // 当前是否准备好可以弹窗了
  ready: false,
  waitDuration: 3000, // 3s
  // 当前弹出的弹窗类型优先级, 0 为没有弹窗
  priority: 0,
  options: {
    ad: false,
    realsport: true,
    voucher: true,
    quest: true,
    virtual: false
  },
  pool: {
    realsport: [],
    virtual: [],
    voucher: [],
    quest: [],
    reset() {
      this.realsport.splice(0)
      this.virtual.splice(0)
      this.voucher.splice(0)
      this.quest.splice(0)
    }
  },

  // 补充订阅参数
  setupPushConfig() {
    const accountId = cookie.get('userId') || cookie.get('accessToken')
    customPush.updateConfig({ accountId })
  },

  // 初始化
  init(options = {}) {
    this.update(options)
    // 首先监听当前是否可以弹窗
    this.watchStatus()
    if (checkLoginStatus()) {
      this.aggregateMessage()
    } else {
      // 刚登录
      this.ready = false
      useEventBus('LOGIN_SUCCESS').on(() => {
        // 已登录
        setSessionLoginStatus(true)
        this.aggregateMessage()
      })
    }

    // 广告弹窗，不受登录控制
    if (this.options.ad) {
      this.getPopupAd()
    }
  },

  // 更新推送
  update(options = {}) {
    const { _topicMap } = this
    for (const key in options) {
      const shouldSub = options[key]
      const listener = this.callbackRef(key)
      if (shouldSub !== this.options[key]) {
        const topic = _topicMap[key]
        if (shouldSub) {
          this.sub(topic, listener)
          if (key === 'ad') {
            this.getPopupAd()
          }
        } else {
          this.unsub(topic)
        }
      }
    }
    this.options = Object.assign(this.options, options)
  },

  // 取消全部设定
  unsetup() {
    this.initSubHub(false)
  },

  updateRedayStatus(visible) {
    if (!visible) {
      this.ready = true
      this.fire()
    } else {
      this.ready = false
    }
  },

  // 监听是否可弹出
  watchStatus() {
    // virtual betslip 主界面或 quickbet 界面展开或隐藏
    if (/virtual/.test(location.pathname)) {
      eventBus.$on('VIRTUAL_BETSLIP_VISIBLE', (visible) => this.updateRedayStatus(visible))
    } else {
      // betslip 主界面或 quickbet 界面展开或隐藏
      eventBus.$on(BETSLIP_VISIBLE, (visible) => this.updateRedayStatus(visible))
    }

    eventBus.$on('product-down', (visible) => this.updateRedayStatus(visible))
  },

  // 聚合推送消息
  async aggregateMessage() {
    this.setupPushConfig()
    // 先上锁
    this.ready = false
    this.initSubHub()
    await sleep(this.waitDuration)
    this.ready = true
    this.fire()
  },

  // 暂停弹出
  pause() {
    this.paused = true
  },

  // 开始弹出
  play() {
    this.paused = false
    this.fire()
  },

  // 初始化订阅
  initSubHub(isSub = true) {
    const exec = isSub ? this.sub : this.unsub
    const { _topicMap } = this
    Object.entries(this.options).forEach(([key, value]) => {
      if (value) {
        const listener = this.callbackRef(key)
        exec(_topicMap[key], listener)
      }
    })
  },

  callFire(data, type) {
    try {
      const info = JSON.parse(data)
      console.log(type, ' push', info)
      this.pool[type].push(info)
      this.fire(type)
    } catch (error) {
      console.error(error)
    }
  },

  // 推送回调函数
  callbackRef(type = '') {
    if (this._ref[type]) return this._ref[type]
    const cb = (data) => this.callFire(data, type)
    this._ref[type] = cb
    return cb
  },

  // 弹出界面
  fire(type) {
    // 当前如果不能弹窗, 消息进入队列
    if (!this.ready || this.paused) {
      return
    }

    let message = null

    if (type) {
      message = this.pool[type].pop()
    } else {
      // 取优先级最高，值最大的消息
      const { realsport, virtual, voucher, quest } = this.pool
      if (realsport.length) {
        // 弹出最大的
        realsport.sort((a, b) => this.compireStringNum(a.totalWinnings, b.totalWinnings))
        console.log('realsport', [...realsport])
        message = realsport.pop()
        type = 'realsport'
      } else if (virtual.length) {
        // 弹出最大的
        virtual.sort((a, b) => this.compireStringNum(a.totalWinnings, b.totalWinnings))
        console.log('virtual', [...virtual])
        message = virtual.pop()
        type = 'virtual'
      } else if (voucher.length) {
        // 弹出最新的
        message = voucher.pop()
        type = 'voucher'
      } else if (quest.length) {
        // 弹出最新的
        message = quest.pop()
        type = 'quest'
      }
    }

    // console.log('message', message)

    const priorityType = this.getPriorityType(type, message)
    const targetPriority = this.getPriority(priorityType)
    const isValid = this.checkPriority(targetPriority)
    isValid && message && this.popup(type, message)
    // 消费一次后或者优先级不足，清空队列
    this.pool.reset()
  },

  // 获取优先级类型
  getPriorityType(type, message) {
    if (!message) return
    if (type === 'realsport' || type === 'virtual') {
      return type
    }
    if (message.template === 1) return 'giveaway'
    return type
  },

  getPriority(type) {
    switch (type) {
      case 'realsport':
        return 1
      case 'virtual':
        return 3
      case 'giveaway':
        return 7
      case 'voucher':
      case 'quest':
        return 9
      default:
        // reset
        return 0
    }
  },

  resetPriority() {
    this.priority = 0
  },

  // 比较优先级, 优先级高或相等，则返回 true
  checkPriority(targetPriority) {
    return this.priority === 0 || targetPriority <= this.priority
  },

  // 比较值大小，排序用
  compireStringNum(a, b) {
    const w1 = +a.replace(/,/g, '')
    const w2 = +b.replace(/,/g, '')
    return w1 - w2
  },

  // 弹出
  popup(type, data) {
    modalHelper.beforeClose()
    // 关闭上一个
    VDialog.close()
    eventBus.$emit('popup_ad_close')

    switch (type) {
      case 'realsport':
      case 'virtual':
        this.popupWinning(type, data)
        break
      case 'voucher':
        // 包含红包和彩蛋
        this.popupVoucherInfo(data)
        break
      case 'quest':
        this.popupQuest(data)
        break
      default:
      // no default
    }

    // lock scroll
    if (data) {
      modalHelper.afterOpen()
    }

    const priorityType = this.getPriorityType(type, data)
    this.priority = this.getPriority(priorityType)
    // 有弹窗了
    this.active = false
    // update balance
    eventBus.$emit('update-balance')
  },

  // realsport/virtual 中奖弹窗
  popupWinning(type, data) {
    if (!data) return
    const isVirtual = type === 'virtual'
    const Comp = isVirtual ? PushWinning : Winning
    VDialog({
      message: vm.$createElement(Comp, { props: { ...data, pushTime: new Date().getTime() } }),
      showClose: !!isVirtual,
      classNames: ['dialog-push-winning', isVirtual ? 'virtual-push-dialog' : 'dialog-push']
    })
      .then(() => {
        modalHelper.beforeClose()
      })
      .catch((err) => {
        modalHelper.beforeClose()
        this.resetPriority()
        console.error(err)
      })
  },

  // 彩蛋/红包 弹窗
  popupVoucherInfo(data) {
    if (!data) return
    const isGiveaway = data.template === 1
    const Comp = isGiveaway ? PopupGiveaway : PopupVoucher
    VDialog({
      message: vm.$createElement(Comp, {
        props: {
          data: { ...data },
          visible: true
        }
      }),
      showClose: false,
      classNames: ['dialog-push', isGiveaway ? 'giveaway-dialog' : 'voucher-dialog']
    })
      .then(() => {
        modalHelper.beforeClose()
      })
      .catch((err) => {
        modalHelper.beforeClose()
        console.error(err)
      })
    if (!isGiveaway) {
      eventBus.$emit('update-bet-vouchers')
    }
  },

  // 任务弹窗
  popupQuest(data) {
    if (!data) return
    VDialog({
      message: vm.$createElement(ConditionalVoucher, {
        props: { quest: data }
      }),
      showClose: false,
      classNames: ['dialog-push']
    })
      .then(() => {
        modalHelper.beforeClose()
      })
      .catch((err) => {
        modalHelper.beforeClose()
        this.resetPriority()
        console.error(err)
      })
  },

  // 订阅
  sub(topic, callback) {
    customPush.sub({ topic, pushType: 'MULTI', listener: callback })
  },

  // 取消订阅
  unsub(topic) {
    customPush.unsub({ topic, pushType: 'MULTI', listener: null })
  },

  // 弹窗广告, 只在首页弹出，优先级最低
  getPopupAd() {
    return !(this.priority > 0)
  }
}

/**
 * 订阅
 * @param {Object}  {
 *    ad: 订阅广告
 *    realsport: 订阅 realsport 中奖推送
 *    voucher: 订阅红包推送
 *    virtual: 订阅 virtual 中奖推送
 *    quest: 红包任务推送
 * }
 * @param {Object}  instance  vue 实例
 * @param {Boolean} force     是否强制订阅
 */
export const subUserPush = ({ ad = false, realsport = true, voucher = true, quest = true, virtual = false } = {}, instance = null) => {
  // 非强制订阅的页面，如果在 app 内，则不订阅推送弹窗
  if (isApp()) {
    return
  }

  if (instance) {
    vm = instance
  }
  amazingPush.init({ ad, realsport, voucher, virtual, quest })
}

// 取消订阅，参数含义同 subUserPush
export const unsubUserPush = () => {
  amazingPush.unsetup()
}

export default amazingPush
